import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { styled } from '@mui/material/styles';
import {
  BOX_BUTTON_SIZE_SPEC,
  BOX_BUTTON_VARIANT_TOKENS,
  type BoxButtonSize,
  type BoxButtonVariant,
} from '@/theme/buttonTokens';

/**
 * 공통 박스 버튼 (디자인 가이드 "Box button").
 * 색상/사이즈는 theme/buttonTokens.ts 스펙을 사용.
 * MUI Button 기본 스타일과 겹치지 않게 일반 <button> 태그로 구현.
 * 아이콘 크기는 사용하는 쪽에서 지정.
 */

export type { BoxButtonSize, BoxButtonVariant };

export type BoxButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> & {
  readonly variant: BoxButtonVariant;
  readonly size?: BoxButtonSize;
  readonly startIcon?: ReactNode;
  readonly endIcon?: ReactNode;
  readonly children?: ReactNode;
};

const Root = styled('button', {
  shouldForwardProp: (p) => p !== 'tone' && p !== 'sizeKey',
})<{ tone: BoxButtonVariant; sizeKey: BoxButtonSize }>(({ tone, sizeKey }) => {
  const tokens = BOX_BUTTON_VARIANT_TOKENS[tone];
  const size = BOX_BUTTON_SIZE_SPEC[sizeKey];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    height: size.height,
    // 세로 flex 부모(flex-shrink 기본 1) 안에서 가이드 높이가 눌리는 것을 막는다.
    // flexShrink:0 대신 minHeight 인 이유 — 가로 배치에서 폭까지 고정되면
    // 좁은 모달 푸터 등에서 버튼이 넘쳐 형제를 밀어낸다
    minHeight: size.height,
    minWidth: size.minWidth,
    padding: `0 ${size.paddingX}px`,
    gap: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: size.fontSize,
    lineHeight: `${size.lineHeight}px`,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition:
      'background-color 120ms ease, border-color 120ms ease, color 120ms ease',

    '& > .v2bb-label': {
      padding: '0 4px',
    },

    // 아이콘 정규화. 인라인 svg 는 베이스라인 아래 디센더 여백을 달고 다녀서
    // 감싼 요소가 아이콘보다 커진다. 그 상태로 중앙 정렬하면 아이콘만 위로 떠 보인다.
    // svg 를 div 로 감싸 넘기는 아이콘(SvgImage 등)까지 닿도록 자손 선택자로 건다
    '& svg': {
      display: 'block',
    },

    background: tokens.enabled.bg,
    borderColor: tokens.enabled.border,
    color: tokens.enabled.color,

    /* 가이드상 focus 는 hover 와 동일 시각 */
    '&:hover, &:focus, &:focus-visible': {
      background: tokens.hover.bg,
      borderColor: tokens.hover.border,
      color: tokens.hover.color,
      outline: 'none',
    },
    '&:active': {
      background: tokens.pressed.bg,
      borderColor: tokens.pressed.border,
      color: tokens.pressed.color,
    },
    '&:disabled': {
      background: tokens.disabled.bg,
      borderColor: tokens.disabled.border,
      color: tokens.disabled.color,
      cursor: 'not-allowed',
    },
  };
});

const BoxButton = forwardRef<HTMLButtonElement, BoxButtonProps>(
  (
    {
      variant,
      size = 'medium',
      startIcon,
      endIcon,
      type = 'button',
      children,
      ...props
    },
    ref,
  ) => (
    <Root ref={ref} type={type} tone={variant} sizeKey={size} {...props}>
      {startIcon}
      {children != null && <span className="v2bb-label">{children}</span>}
      {endIcon}
    </Root>
  ),
);

BoxButton.displayName = 'BoxButton';

export default BoxButton;
